/**
 * FullPlayer — 全螢幕播放器（設計 v2 §4 **C11**）
 *
 * 來源：`.coappery/tasks/J-ui-cosingery/design/drawer.jsx` C11（UI v2 **步 8**）
 *   · 進度／上下首／隨機／循環／速度／音量／我的最愛／加入清單（→ `PlayerExtras`）
 *   · 同步歌詞（C12 → `SyncedLyrics`）；⭐ **保留**「你寫嘅日記」區（`decisions/036`）
 *   · ⚠️ 照實原則：冇歌詞 → 照實講；高音質切唔到 → 照實報（`rules.md` B2／B5）
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlayer } from './PlayerContext';
import { useLibrary } from '../hooks/useLibrary';
import { formatTime } from './playerModel';
import { Icons } from '../components/Icons';
import SyncedLyrics from './SyncedLyrics';
import PlayerExtras from './PlayerExtras';

export interface FullPlayerProps {
  /** 收起（返去清單） */
  onClose: () => void;
}

export default function FullPlayer({ onClose }: FullPlayerProps) {
  const { t } = useTranslation();
  const p = usePlayer();
  const { songs } = useLibrary();
  const [note, setNote] = useState<string | null>(null);

  const track = p.track;
  /** 冇歌 → 唔渲染（由呼叫者決定去邊） */
  if (!track) return null;

  /** ⭐ `decisions/036`：而家播緊嗰首嘅日記（長文慢慢滾） */
  const journal = (songs.find((s) => s.id === track.id)?.journal ?? '').trim();
  const diaryLong = journal.length > 90;

  const secs = (p.durationMs ?? 0) / 1000;
  const pos = p.positionMs / 1000;
  const repeatKey = p.repeat === 'one' ? 'player_repeat_one' : p.repeat === 'all' ? 'player_repeat_all' : 'player_repeat_off';

  return (
    <div className="player player--open" role="dialog" aria-modal="true" aria-label={t('player_now_playing')}>
      <div className="player__head">
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('sheet_close_aria')}>
          <Icons.ChevronDown />
        </button>
        <span className="player__label">{t('player_now_playing')}</span>
        <span style={{ width: 'var(--tap-min)' }} aria-hidden="true" />
      </div>

      <div className="player__title">
        <h2>{track.title}</h2>
        <span className="footnote">{p.hifi ? t('player_hifi_on') : t('player_hifi_off')}</span>
      </div>

      {/* ⭐ `decisions/036`：**上＝你寫嘅日記**（長文慢慢滾）—— 設計 v2 保留（唔准消失） */}
      <div className="player__diary" data-testid="player-diary-view">
        <p className="footnote">{t('player_diary_label')}</p>
        <div
          data-testid="player-diary-words"
          className={diaryLong ? 'diary-autoscroll' : undefined}
          style={{
            fontFamily: 'var(--font-hand, inherit)',
            fontSize: 'var(--font-size-body, 18px)',
            lineHeight: 'var(--diary-line, 28px)',
            animationDuration: diaryLong ? `${Math.max(24, Math.round(journal.length / 3))}s` : undefined,
          }}
        >
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{journal || t('diary_write_placeholder')}</p>
          {diaryLong ? (
            <p aria-hidden style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {journal}
            </p>
          ) : null}
        </div>
      </div>

      {/* ⚠️ 2026-10-11：歌詞**唔跟唱**（實測對唔到拍；provider 冇時間碼 → 設計 R-02）→ 純閱讀、自己捲 */}
      <SyncedLyrics lyrics={track.lyrics} />

      <div className="player__deck">
        <div className="seek">
          <input
            type="range"
            min={0}
            max={secs > 0 ? secs : 1}
            step={1}
            value={pos}
            onChange={(e) => p.seek(Number(e.target.value) * 1000)}
            aria-label={t('player_seek')}
            aria-valuetext={`${formatTime(p.positionMs)} / ${formatTime(p.durationMs)}`}
          />
          <div className="seek__time">
            <span>{formatTime(p.positionMs)}</span>
            <span>{formatTime(p.durationMs)}</span>
          </div>
        </div>

        <div className="transport">
          <button
            type="button"
            data-testid="player-shuffle"
            className={'icon-btn' + (p.shuffle ? ' icon-btn--on' : '')}
            aria-pressed={p.shuffle}
            aria-label={p.shuffle ? t('player_shuffle_on') : t('player_shuffle_off')}
            onClick={p.toggleShuffle}
          >
            <Icons.Shuffle />
          </button>
          <button type="button" className="icon-btn" onClick={p.prev} aria-label={t('player_prev')}>
            <Icons.Prev />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={p.toggle}
            aria-label={p.playing ? t('player_pause') : t('player_play')}
          >
            {p.playing ? <Icons.Pause /> : <Icons.Play />}
          </button>
          <button type="button" className="icon-btn" onClick={p.next} aria-label={t('player_next')}>
            <Icons.Next />
          </button>
          <button
            type="button"
            data-testid="player-repeat"
            className={'icon-btn' + (p.repeat !== 'off' ? ' icon-btn--on' : '')}
            aria-pressed={p.repeat !== 'off'}
            aria-label={t(repeatKey)}
            onClick={p.cycleRepeat}
          >
            {p.repeat === 'one' ? <Icons.RepeatOne /> : <Icons.Repeat />}
          </button>
        </div>

        <PlayerExtras onNote={setNote} />
      </div>

      {note ? (
        <p className="footnote" role="status" style={{ textAlign: 'center', paddingBottom: 'var(--space-2)' }}>
          {note}
        </p>
      ) : null}
    </div>
  );
}
